import React, { useState, useEffect } from "react";
import { ReviewsContext, ReviewsProps } from "./Context";
import ReviewModal from "./ReviewModal";
import { captureImpression } from 'javascripts/impressions/capture_impression.js';
import {COMPANY_NAME, JSON_HEADERS, LEADGEN_TOGGLE_RESET_TIME} from "../../constants/constants";
import { fetchReviews } from "../../utils/postingHelpers";
import RegistrationRoot from "../users/RegistrationRoot";
import ResponseModal from "../modals/ResponseModal";
import { updateReviewAccessCount } from "../../utils/postingHelpers";
import LeadGen from "../leads/LeadGen";
import AudioModal from "../modals/AudioModal";
import { createLead } from "javascripts/utils/createLead.js";

function ReviewModalRoot(props) {
    const [reviews, setReviews] = useState(props.reviews); // reviews on the page that could trigger a modal
    const [selectedReview, setSelectedReview] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewsByProduct, setReviewsByProduct] = useState({});
    const [selectedReviewHistory, setSelectedReviewHistory] = useState([]);
    const [selectedReviewProduct, setSelectedReviewProduct] = useState(props.product);
    const [hasMoreReviews, setHasMoreReviews] = useState(true);
    const [isForwardClick, setIsForwardClick] = useState(false);

    // test restricted access to reviews
    const [reviewAccessCount, setReviewAccessCount] = useState(props.review_access_count || 0);
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [registrationRequired, setRegistrationRequired] = useState(props.registration_required);
    const [currentUser, setCurrentUser] = useState(props.current_user);
    const [showRegistrationConfirmation, setShowRegistrationConfirmation] = useState(false);

    const [leadAttributes, setLeadAttributes] = useState(props.lead_attributes);
    const [showLeadGen, setShowLeadGen] = useState(false);
    const [leadDetails, setLeadDetails] = useState({});

    const [selectedAudio, setSelectedAudio] = useState({});
    const [showAudioModal, setShowAudioModal] = useState(false);

    const tooltips = props.tooltips;

    const fetchReviewIds = async () => {
        if(!Object.keys(reviewsByProduct).includes(selectedReviewProduct.id.toString())) {
            try {
                const response = await fetch(`/products/${selectedReviewProduct.id}/product_review_ids`, {
                    method: 'GET',
                    headers: JSON_HEADERS,
                });
                const jsonResponse = await response.json();
                if (jsonResponse.reviews) {
                    setReviewsByProduct({...reviewsByProduct, [selectedReviewProduct.id]: jsonResponse.reviews});
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    const fetchSingleReview = async (reviewId) => {
        let reviews = await fetchReviews([reviewId], 'display_data,lead_gen_data');
        let review = reviews?.[0];
        review && captureImpression(review.actions.url);
        setSelectedReviewHistory([...selectedReviewHistory, selectedReview]);
        setSelectedReview(review);
        setShowReviewModal(true);

    };

    useEffect(() => {
        // if user triggers the modal, start to get all the reviews
        if(showReviewModal && selectedReview && props.fetch_reviews) {
            fetchReviewIds();
        }

        // testing restricted review access
        if(registrationRequired && showReviewModal && reviewAccessCount) {
            updateReviewAccessCount(props.token);
            setTimeout(() => setShowRegistrationModal(true), 3000);
        }
    }, [showReviewModal, reviewAccessCount]);


    const handleReviewModalToggle = (e) => {
        if (e.target.tagName === 'A' && e.target.href.length && new URLSearchParams(new URL(e.target.href).search).has('review_id')) {
            e.preventDefault();
            const reviewId = +new URLSearchParams(new URL(e.target.href).search).get('review_id');
            let review = reviews.find((r) => r.id === reviewId);
            review && captureImpression(review.actions.url);
            setSelectedReview(review);
            setSelectedReviewProduct(review.product || props.product);
            setShowReviewModal(true);
            // restricted review access
            setReviewAccessCount((c) => c + 1);
        }
    }

    const handleReviewModalToggleOnForward = (reviewId) => {
        if(reviewId) {
            let review = props.reviews.find((r) => r.id === reviewId) || allReviews.find((r) => r.id === reviewId);
            setSelectedReview(review);
            setShowReviewModal(true);
            setIsForwardClick(true);
        }
    }

    useEffect(() => {
        // Attach event listener to document to handle a review modal toggle outside of the react app
        document.addEventListener('click', handleReviewModalToggle);
    }, []);

    const onNextReviewToggle = () => {
        if(selectedReview) {
            let reviewIds = reviewsByProduct[selectedReviewProduct.id];
            if(reviewIds) {
                let nextIndex = reviewIds.indexOf(selectedReview.id) + 1;
                if (nextIndex < reviewIds.length) {
                    setHasMoreReviews(nextIndex != (reviewIds.length - 1));
                    let reviewId = reviewIds[nextIndex];
                    if (reviewId) {
                        // restricted review access
                        setReviewAccessCount((c) => c + 1);
                        fetchSingleReview(reviewId);
                        replaceReviewURL(reviewId);
                        return;
                    }
                }
            }
        }
        onCloseReviewModal()
    }

    const onPrevReviewToggle = () => {
        let readReviews = selectedReviewHistory.length;
        if(readReviews) {
            const prevReview = selectedReviewHistory[readReviews - 1];
            setSelectedReviewHistory(selectedReviewHistory.slice(0, -1));
            setHasMoreReviews(true);
            setSelectedReview(prevReview);
            replaceReviewURL(prevReview.id);
        } else onCloseReviewModal();
    }

    const onCloseReviewModal = (replaceURL=true) => {
        setSelectedReview(null);
        setShowReviewModal(false);
        setIsForwardClick(false);
        if(replaceURL) {
            // replaceReviewURL();
            history.back(); // reset history state since the browser's back button was not clicked
        }
    }

    const replaceReviewURL = (reviewId=null) => {
        const queryParams = new URLSearchParams(location.search);
        if(reviewId) {
            queryParams.set('review_id', reviewId);
        } else {
            queryParams.delete('review_id');
            queryParams.delete('tid');
        }
        let newUrl = new URL(location);
        newUrl.search = queryParams;
        history.replaceState(null, '', newUrl.href);
    }

    const onCommentsChangeHandler = (reviewId, comments, commentCount) => {
        setReviews(() => reviews.map(review => {
            if (review.id === reviewId) {
                return { ...review, comments: comments, comment_count: commentCount };
            }
            return review;
        }));
        if(selectedReview) setSelectedReview({ ...selectedReview, comments: comments, comment_count: commentCount });
    }

    // audio
    const onCloseAudio = () => {
        setSelectedAudio({});
        setShowAudioModal(false);
    }

    const onToggleAudio = (url, type, reviewId = null) => {
        if (reviewId) {
            createLead({ leadType: 'ProductReviewLead', leadTypeData: reviewId, leadCode: "AUDIO_HIGHLIGHT", token: props.token });
        }
        setSelectedAudio({ url: url, type: type});
        setShowAudioModal(true);
    }

    const renderAudioModal = () => {
        if(showAudioModal) {
            return (
                <AudioModal
                    show={showAudioModal}
                    audioFile={selectedAudio.url}
                    mimeType={selectedAudio.type}
                    onHide={onCloseAudio}
                    text={'Listen to the interview'}
                />
            )
        }
    }

    // leadgen functions
    const onLeadCreation = (action='', actionData, responseData={}) => {
        if(!currentUser && responseData.user) {
            setCurrentUser(responseData.user);
        }
        switch(action) {
            case 'AUDIO_HIGHLIGHT':
                // set up LeadGen with actionData.selected if we need phone verification, etc...
                setSelectedAudio(actionData.selected); // corresponds to the selected audio { url, type, reviewId }
                setShowAudioModal(true);
                return;
            case 'DOWNLOAD_REPORT':
            case 'INQUIRY_CALL':
            case 'PEERANALYST_USERS':
                return;
        }
    }

    const onToggleLeadGen = (leadData={}) => {
        setLeadDetails({ ...leadData });
        setShowLeadGen(true);
        setTimeout(() => { setShowLeadGen(false) }, LEADGEN_TOGGLE_RESET_TIME);
    }

    const renderLeadGenModal = () => {
        // For external toggle lead gen only
        // This components gets passed in custom lead details, including:
        // Generic fields: hiddenTags, leadCode, cta, actionParams,  gtmEventRegion, gtmEventElement, modalOverlay, withConfirmationScreen
        // PeerTalk fields: actionParams, confirmationParams, modalHeader, modalSubheader, includePeerTalk
        // Report download fields: directReportUrl, directReportDownload, reportTopic, sendReport
        // Audio fields including: actionParams for the selected audio
        if(Object.keys(leadDetails).length) {
            return (
                <LeadGen
                    {...leadAttributes}
                    key={leadDetails?.leadCode}
                    token={props.token}
                    updateRoot={true}
                    currentUser={currentUser}
                    updateCurrentUser={setCurrentUser}
                    emailOnly={true}
                    externalToggle={true}
                    toggleLeadGen={showLeadGen}
                    modalHeader={'Get full access to PeerSpot'}
                    modalSubheader={'Register to unlock all PeerSpot features'}
                    afterLeadCreation={onLeadCreation}
                    redirect={false}
                    {...leadDetails}
                />
            )
        }
    }

    // testing restricted review access

    const onHideRegistrationModal = (_) => {
        if(reviewAccessCount >= 2 && !currentUser) {
            onCloseReviewModal();
        }
        setShowRegistrationModal(false);
        if(currentUser) {
            setRegistrationRequired(false);
            setShowRegistrationConfirmation(true);
        }
    }

    const renderRegistrationModal = () => {
        if(registrationRequired && selectedReview) {
            let subHeader = selectedReviewProduct.review_count < 20 ?
                `Get access to the full review and to many more peer reviews` :
                `Get access to the full review and to ${selectedReviewProduct.review_count - 1} more ${selectedReviewProduct.name} reviews`;
            return (
                <RegistrationRoot
                    show={showRegistrationModal}
                    token={props.token}
                    gdpr={props.registration.gdpr}
                    modalHeader={'Sign up to continue reading'}
                    modalSubheader={subHeader}
                    allowSkip={reviewAccessCount < 2}
                    cta={`Join ${COMPANY_NAME}`}
                    onClose={onHideRegistrationModal}
                    onRegister={setCurrentUser}
                    reCaptchaKey={props.reCaptchaKey}
                    passwordRule={props.registration.password_rule}
                />
            )
        }
    }

    const renderRegistrationConfirmation = () => {
        if(showRegistrationConfirmation) {
            return (
                <ResponseModal
                    bsPrefix={'modal-overlay'}
                    bgColor={'soft-success'}
                    textColor={'success'}
                    showIcon={false}
                    message={'Thank you for registering PeerSpot, you can now access all reviews across the site'}
                    show={showRegistrationConfirmation}
                    centered={false}
                    textAlignment={'start'}
                    btnClose={false}
                    btnColor={'success'}
                    borderColor={'success'}
                    dialogClasses={'modal-bottom-notice'}
                    showBackdrop={false}
                    autoClose={true}
                    handleShow={setShowRegistrationConfirmation} />
            )
        }
    }

    const renderReviewModal = () => {
        if(showReviewModal) {
            return (
                <ReviewModal
                    review={selectedReview}
                    product={selectedReviewProduct}
                    relatedProducts={selectedReview.related_products || props.related_products}
                    reviewSections={props.review_sections}
                    show={showReviewModal}
                    report={props.report}
                    token={props.token}
                    reviewCount={selectedReviewProduct?.review_count || props.product?.review_count}
                    onCancel={onCloseReviewModal}
                    onNext={onNextReviewToggle}
                    onPrev={onPrevReviewToggle}
                    hasMoreReviews={hasMoreReviews}
                    onCommentsChangeHandler={onCommentsChangeHandler}
                    isShareLink={false}
                    onReplaceReviewURL={replaceReviewURL}
                    forwardClick={isForwardClick}
                    forwardModalToggle={handleReviewModalToggleOnForward}
                    restrictedAccess={showRegistrationModal}
                />
            )
        }
    }

    const contextValue = {
        leadAttributes,
        selectedAudio,
        currentUser,
        setCurrentUser,
        tooltips,
        onLeadCreation,
        onToggleAudio,
        onToggleLeadGen }

    return (
        <ReviewsContext.Provider value={ contextValue }>
            {renderReviewModal()}
            {renderRegistrationModal()}
            {renderRegistrationConfirmation()}
            {renderLeadGenModal()}
            {renderAudioModal()}
        </ReviewsContext.Provider>
    )
}
ReviewModalRoot.displayName = 'ReviewModalRoot';
export default ReviewModalRoot;
